import React, { useState, useRef, useEffect } from 'react'
import { HelpCircle, Info } from 'lucide-react'
import { cn } from '../../lib/utils'

interface TooltipProps {
  content: string | React.ReactNode
  children?: React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  iconClassName?: string
  variant?: 'help' | 'info'
  showIcon?: boolean
  maxWidth?: string
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  className,
  iconClassName,
  variant = 'help',
  showIcon = true,
  maxWidth = 'max-w-xs sm:max-w-sm md:max-w-md'
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [actualPosition, setActualPosition] = useState(position)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Check if tooltip would overflow viewport and adjust position
      let newPosition = position

      if (position === 'top' && triggerRect.top - tooltipRect.height < 0) {
        newPosition = 'bottom'
      } else if (position === 'bottom' && triggerRect.bottom + tooltipRect.height > viewportHeight) {
        newPosition = 'top'
      } else if (position === 'left' && triggerRect.left - tooltipRect.width < 0) {
        newPosition = 'right'
      } else if (position === 'right' && triggerRect.right + tooltipRect.width > viewportWidth) {
        newPosition = 'left'
      }

      setActualPosition(newPosition)
    }
  }, [isVisible, position])

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-3',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-3',
    left: 'right-full top-1/2 -translate-y-1/2 mr-3',
    right: 'left-full top-1/2 -translate-y-1/2 ml-3'
  }

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 -mt-px',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-px',
    left: 'left-full top-1/2 -translate-y-1/2 -ml-px',
    right: 'right-full top-1/2 -translate-y-1/2 -mr-px'
  }

  const arrowBorderClasses = {
    top: 'border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent',
    left: 'border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent',
    right: 'border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent'
  }

  const Icon = variant === 'help' ? HelpCircle : Info

  return (
    <div className="relative inline-flex items-center">
      <div
        ref={triggerRef}
        className="inline-flex items-center"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children}
        {showIcon && !children && (
          <Icon 
            className={cn(
              "w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors",
              iconClassName
            )}
          />
        )}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={cn(
            "absolute z-50 pointer-events-none animate-in fade-in zoom-in-95 duration-200",
            positionClasses[actualPosition]
          )}
        >
          <div
            className={cn(
              "relative bg-gray-900 text-white text-sm rounded-lg shadow-xl",
              "px-4 py-3 min-w-[200px]", // Added min-width for better display
              maxWidth,
              className
            )}
          >
            <div className="leading-relaxed whitespace-normal">
              {content}
            </div>
            {/* Arrow */}
            <div
              className={cn(
                "absolute w-0 h-0 border-[6px]",
                arrowClasses[actualPosition],
                arrowBorderClasses[actualPosition]
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// Compound component for inline help text
export const HelpTooltip: React.FC<{ 
  text: string
  className?: string
  maxWidth?: string
}> = ({ text, className, maxWidth = 'max-w-xs sm:max-w-sm md:max-w-md' }) => {
  return (
    <Tooltip 
      content={text}
      variant="help"
      className={className}
      maxWidth={maxWidth}
    />
  )
}

// Compound component for info tooltips
export const InfoTooltip: React.FC<{ 
  text: string
  className?: string
  maxWidth?: string
}> = ({ text, className, maxWidth = 'max-w-xs sm:max-w-sm md:max-w-md' }) => {
  return (
    <Tooltip 
      content={text}
      variant="info"
      className={className}
      maxWidth={maxWidth}
    />
  )
}